import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { StaffRolesMap } from './context/StaffRolesContext';

interface ServiceDashboardProps {
  activeServices: string[];
  staffRoles: StaffRolesMap;
  servicesLoading: boolean;
  servicesError: string | null;
}

const SERVICE_ROUTES: Record<string, string> = {
  events: '/events/',
  'digital-media': '/digital-media/',
};

const HERO_IMAGE_URL =
  'https://lh3.googleusercontent.com/aida/ADBb0ujFYAF_wY8LOr_JL5i2vEKIcdlQnmuyo0FO0z9MsgTxeTeNMiHcQkRGNVeaZfoFsyg_otcU2LRNb93fqXOL6cJKs0Zih0pKJjcBi80ho6l4LfQtRouierCAXRBYvcbB4usHECZRm0rOA7EoOOkCxYZWicdbVSeNL5Zze4cumW_Vnj73M_gXt3HDO2XGlG4QuEFN5Fu4hBah3JCKAXvByHfzPr-OrjoF5xZG6_Rh8YAszaZyjV36oT8PVh8_X3I8AMOXU3GD2Dowvls';
const RETREATS_IMAGE_URL =
  'https://lh3.googleusercontent.com/aida/ADBb0uiwMzLRFtMseasqlzUEODlTuQa5UPFZMMFWMg2aHL2D5QSjaTtgw0YqInTSZ_5ZbL43qBDeqxYDyoR6C6t_lvrHxbYDV1t5siXwlfksk3uqi8VL9lY4yOSgsNUTwdTM1LkNSHu6LuC0Em37aMc2AyIaWnr5tyYegf6MnklK3ClPfW4sTUM8GVrHFd1wNDEApAYvnuGCVU1smSIxZcVsFfjlDi9ncCOA9tFwLZhCeH9nZEEbh7xeYjnsI0hlxXE1YwmUxxRuS_xuoAM';
const NAVIGATOR_IMAGE_URL =
  'https://lh3.googleusercontent.com/aida/ADBb0ujiAkl82NpU947HyU6CTJN-DPYiXEIGTR4hxX2rQkyDEw73TzguMqKysU4ANJFkMtVMiWecWokNCmnvWSzVQk3WQur_4hLm1K1FFkgh75jIYqbg9MDf3O3yr90KrOZiID4kTfmQU98vfw1GOzPSBX4u8k8ktgPOWPmvLdyWOFhGI6nDdaQp9iCOwxmm3KjoIxzRkzCg2Zw7GrYVhXQoO7kwmYufLgz2iEMMecdEADx1nymiOhHw8Kx5CqvqwuVs_7fzk8ZwVJeLqfQ';

export default function ServiceDashboard({
  activeServices,
  staffRoles,
  servicesLoading,
  servicesError,
}: ServiceDashboardProps) {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get('service')?.trim().toLowerCase() ?? '';
  const hasDigitalMedia = activeServices.includes('digital-media');

  const navigateToMaterials = () => {
    if (!hasDigitalMedia) {
      return;
    }
    navigate('/digital-media/');
  };

  const navigateToRetreats = () => {
    if (!hasDigitalMedia) {
      return;
    }
    navigate('/digital-media/?category=retreat-announces');
  };

  const navigateToNavigator = () => {
    navigate('/navigator/');
  };

  useEffect(() => {
    if (!requestedService || servicesLoading || servicesError) {
      return;
    }
    const route = SERVICE_ROUTES[requestedService];
    if (!route) {
      return;
    }
    if (!activeServices.includes(requestedService)) {
      return;
    }
    navigate(route, { replace: true });
  }, [activeServices, navigate, requestedService, servicesError, servicesLoading]);

  return (
    <div className="relative min-h-screen overflow-x-hidden bg-background text-on-background antialiased selection:bg-primary-container selection:text-on-primary-container">
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -left-[10%] -top-[10%] h-[50vw] w-[50vw] rounded-full bg-primary-container/20 blur-[100px]" />
        <div className="absolute -bottom-[10%] -right-[10%] h-[60vw] w-[60vw] rounded-full bg-secondary-container/20 blur-[120px]" />
      </div>

      <header className="fixed top-0 z-50 w-full border-b border-white/10 bg-[#0d0c4b]/80 backdrop-blur-xl shadow-[0_4px_30px_rgba(0,0,0,0.1)]">
        <div className="flex h-16 w-full items-center justify-between px-5">
          <button
            type="button"
            className="rounded-full p-2 text-white/70 transition-opacity duration-200 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary-container/50 active:scale-95"
          >
            <span className="material-symbols-outlined">auto_awesome</span>
          </button>
          <h1 className="font-headline text-[24px] font-bold uppercase tracking-tight text-primary-container">
            KK
          </h1>
          <button
            type="button"
            className="rounded-full p-2 text-white/70 transition-opacity duration-200 hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary-container/50 active:scale-95"
          >
            <span className="material-symbols-outlined">account_circle</span>
          </button>
        </div>
      </header>

      <main className="relative z-10 flex flex-col gap-10 px-5 pb-32 pt-24">
        <section className="flex flex-col items-center gap-1 text-center">
          <p className="font-label-mono text-sm uppercase tracking-[0.22em] text-primary-container">
            {t('main.badge')}
          </p>
          <h2 className="font-headline text-[24px] font-semibold text-on-background">{t('main.title')}</h2>
        </section>

        <section className="relative aspect-[4/3] w-full overflow-hidden rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <img
            alt={t('main.heroAlt')}
            src={HERO_IMAGE_URL}
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
          <div className="absolute bottom-0 left-0 flex w-full flex-col gap-2 p-4">
            <span className="w-fit rounded-full border border-white/10 bg-[#523154] px-3 py-1 font-label-mono text-[10px] uppercase tracking-wider text-primary-container shadow-[0_0_10px_rgba(255,150,104,0.2)]">
              {t('main.heroPill')}
            </span>
            <h3 className="whitespace-pre-line font-headline text-[20px] leading-snug text-on-background">{t('main.heroTitle')}</h3>
          </div>
          <div className="absolute right-3 top-3 flex gap-1">
            <div className="h-1 w-8 rounded-full bg-primary-container shadow-[0_0_8px_#FF9668]" />
            <div className="h-1 w-8 rounded-full bg-white/20" />
            <div className="h-1 w-8 rounded-full bg-white/20" />
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={navigateToMaterials}
            className="glass-panel group relative flex aspect-square flex-col items-center justify-center gap-3 overflow-hidden rounded-xl p-0 transition-transform duration-300 active:scale-[0.98]"
          >
            <div className="absolute inset-0 bg-gradient-radial from-primary-container/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-surface-container-high shadow-inner">
              <span className="material-symbols-outlined text-2xl text-primary-container">library_books</span>
            </div>
            <span className="relative z-10 text-base font-medium text-on-background">{t('main.materials')}</span>
          </button>

          <button
            type="button"
            onClick={navigateToRetreats}
            className="glass-panel group relative flex aspect-square flex-col items-center justify-center gap-3 overflow-hidden rounded-xl p-0 transition-transform duration-300 active:scale-[0.98]"
          >
            <div className="absolute inset-0">
              <img alt={t('main.retreatsAlt')} src={RETREATS_IMAGE_URL} className="h-full w-full object-cover opacity-30 mix-blend-overlay" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-background/50 to-background/90" />
            <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-surface-container-high shadow-inner">
              <span className="material-symbols-outlined text-2xl text-tertiary">event_available</span>
            </div>
            <span className="relative z-10 text-base font-medium text-on-background">{t('main.retreats')}</span>
          </button>

          <button
            type="button"
            className="glass-panel group relative flex aspect-square flex-col items-center justify-center gap-3 overflow-hidden rounded-xl p-0 transition-transform duration-300 active:scale-[0.98]"
          >
            <div className="absolute inset-0 bg-gradient-radial from-secondary/10 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-surface-container-high shadow-inner">
              <span className="material-symbols-outlined text-2xl text-secondary">forum</span>
            </div>
            <span className="relative z-10 text-base font-medium text-on-background">{t('main.chat')}</span>
            <div className="absolute right-3 top-3 h-3 w-3 rounded-full bg-error shadow-[0_0_10px_rgba(255,180,171,0.6)]" />
          </button>

          <button
            type="button"
            onClick={navigateToNavigator}
            className="glass-panel group relative flex aspect-square flex-col items-center justify-center gap-3 overflow-hidden rounded-xl p-0 transition-transform duration-300 active:scale-[0.98]"
          >
            <div className="absolute inset-0">
              <img alt={t('main.navigatorAlt')} src={NAVIGATOR_IMAGE_URL} className="h-full w-full object-cover opacity-30 mix-blend-overlay" />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-background/50" />
            <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-surface-container-high shadow-inner">
              <span className="material-symbols-outlined text-2xl text-primary">explore</span>
            </div>
            <span className="relative z-10 text-base font-medium text-on-background">{t('main.navigator')}</span>
          </button>
        </section>

        <section className="glass-panel relative overflow-hidden rounded-xl p-6">
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary-container/10 blur-2xl" />
          <div className="relative z-10 flex flex-col gap-2">
            <div className="flex items-center gap-1 text-white/50">
              <span className="material-symbols-outlined text-sm">self_improvement</span>
              <span className="font-label-mono text-[10px] uppercase tracking-[0.2em]">{t('main.affirmationBadge')}</span>
            </div>
            <p className="font-headline text-[18px] italic leading-relaxed text-on-background">{t('main.affirmation')}</p>
          </div>
        </section>

        {servicesError ? (
          <section className="glass-panel rounded-xl p-4 text-sm text-error">{servicesError}</section>
        ) : null}

        {servicesLoading ? (
          <section className="glass-panel rounded-xl p-4 text-sm text-on-surface-variant">{t('dashboard.loading')}</section>
        ) : null}

        {Object.keys(staffRoles).length > 0 ? (
          <section className="glass-panel rounded-xl p-4">
            <p className="mb-3 font-label-mono text-[10px] uppercase tracking-[0.2em] text-on-surface-variant">{t('staff.title')}</p>
            <div className="grid gap-2">
              {staffRoles.events?.isManager ? (
                <button
                  type="button"
                  onClick={() => navigate('/events/manager/')}
                  className="flex items-center gap-3 rounded-xl bg-surface-container-high/70 px-4 py-3 text-left text-on-background"
                >
                  <span className="material-symbols-outlined text-primary-container">calendar_month</span>
                  <span>{t('staff.events.manager.title')}</span>
                </button>
              ) : null}
            </div>
          </section>
        ) : null}
      </main>

      <nav className="fixed bottom-0 z-50 flex w-full items-center justify-around rounded-t-lg border-t border-white/10 bg-[#0d0c4b]/80 px-4 pb-[calc(env(safe-area-inset-bottom,20px)+12px)] pt-3 shadow-[0_-4px_20px_rgba(255,150,104,0.1)] backdrop-blur-xl md:hidden">
        <button type="button" className="group relative flex w-16 flex-col items-center justify-center text-primary-container after:absolute after:-bottom-1 after:h-1 after:w-1 after:rounded-full after:bg-primary-container after:shadow-[0_0_8px_#FF9668]">
          <span className="material-symbols-outlined mb-1 transition-transform group-hover:-translate-y-1" style={{ fontVariationSettings: "'FILL' 1" }}>home</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider">{t('footer.home')}</span>
        </button>
        <button
          type="button"
          onClick={navigateToMaterials}
          className={`group flex w-16 flex-col items-center justify-center transition-colors duration-200 ${hasDigitalMedia ? 'text-white/40 hover:text-white/90' : 'cursor-not-allowed text-white/20'}`}
        >
          <span className="material-symbols-outlined mb-1 transition-transform group-hover:-translate-y-1">library_books</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider">{t('footer.materials')}</span>
        </button>
        <button
          type="button"
          onClick={navigateToRetreats}
          className={`group flex w-16 flex-col items-center justify-center transition-colors duration-200 ${hasDigitalMedia ? 'text-white/40 hover:text-white/90' : 'cursor-not-allowed text-white/20'}`}
        >
          <span className="material-symbols-outlined mb-1 transition-transform group-hover:-translate-y-1">event_available</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider">{t('footer.retreats')}</span>
        </button>
        <button
          type="button"
          onClick={navigateToNavigator}
          className="group flex w-16 flex-col items-center justify-center text-white/40 transition-colors duration-200 hover:text-white/90"
        >
          <span className="material-symbols-outlined mb-1 transition-transform group-hover:-translate-y-1">explore</span>
          <span className="text-[10px] font-semibold uppercase tracking-wider">{t('footer.navigator')}</span>
        </button>
      </nav>
    </div>
  );
}
